import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useSectionTracking } from "./AnalyticsProvider";
import { useLanguage } from "./LanguageProvider";

export default function SpecimenStats() {
  const sectionRef = useSectionTracking("specimen");
  const { t } = useLanguage();
  const s = t.specimen;
  const animRef = useRef(null);
  const inView = useInView(animRef, { once: true, margin: "-80px" });

  const stats = [
    { value: "1,055.05", unit: s.carats, desc: s.rough },
    { value: "211", unit: s.grams, desc: s.weight },
    { value: "82 x 43 x 36", unit: s.mm, desc: s.dimensions },
    { value: "Muzo", unit: "Colombia", desc: s.deposit },
    { value: "VSbG", unit: s.colour, desc: s.vsbg },
    { value: "None", unit: s.enhancement, desc: s.untreated },
  ];

  return (
    <section id="specimen" ref={sectionRef} className="relative py-12 lg:py-18" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12" ref={animRef}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <p className="overline mb-4">{s.overline}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F4F0EA] font-light tracking-tighter">
            {s.title} <span className="italic text-[#C5A059]">{s.title_accent}</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-[#C5A059]/15">
          {/* Left stats column */}
          <div className="lg:col-span-3 flex flex-col">
            {stats.slice(0, 3).map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
                className="p-6 lg:p-8 border-b lg:border-b border-[#C5A059]/10 last:border-b-0 lg:last:border-b"
                data-testid={`stat-card-${i}`}
              >
                <div className="font-serif text-2xl sm:text-3xl text-[#F4F0EA] tracking-tight mb-1">{stat.value}</div>
                <div className="text-xs tracking-[0.15em] uppercase text-[#C5A059]/60 mb-1">{stat.unit}</div>
                <p className="text-sm text-[#B8B3AA]/75 font-light">{stat.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Central hero image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 1, delay: 0.3 }}
            className="lg:col-span-6 relative border-x-0 lg:border-x border-[#C5A059]/10 min-h-[400px] lg:min-h-[500px] bg-[#111110] overflow-hidden"
          >
            <img
              src="/media/photos/museum_open.jpg"
              alt="The Bridgette Emerald — open display case"
              className="w-full h-full object-cover"
            />
          </motion.div>

          {/* Right stats column */}
          <div className="lg:col-span-3 flex flex-col">
            {stats.slice(3).map((stat, i) => (
              <motion.div
                key={i + 3}
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.4 + i * 0.1 }}
                className="p-6 lg:p-8 border-b lg:border-b border-[#C5A059]/10 last:border-b-0 lg:last:border-b"
                data-testid={`stat-card-${i + 3}`}
              >
                <div className="font-serif text-2xl sm:text-3xl text-[#F4F0EA] tracking-tight mb-1">{stat.value}</div>
                <div className="text-xs tracking-[0.15em] uppercase text-[#C5A059]/60 mb-1">{stat.unit}</div>
                <p className="text-sm text-[#B8B3AA]/75 font-light">{stat.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-6 flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-8"
        >
          <p className="text-sm text-[#B8B3AA]/70 max-w-md font-light">{s.desc}</p>
          <p className="text-sm text-[#B8B3AA]/65 italic max-w-md font-light border-l border-[#C5A059]/15 pl-4">
            Formed 30–38 million years ago in the Cordillera Oriental of the Colombian Andes. 
            Gübelin's analysis identified three-phase fluid inclusions with jagged outlines — the geological fingerprint 
            that confirms Muzo origin with scientific certainty.
            <span className="block text-xs text-[#B8B3AA]/35 mt-2 not-italic">Source: Gübelin Gemological Profile</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
